<template>
  <v-list>
    <v-list-subheader class="text-caption" color="info">
      {{ $t("strings.allowed-info") }}
      <v-tooltip activator="parent" location="top">
        {{ $t("strings.allowed-info") }}
      </v-tooltip>
    </v-list-subheader>
    <v-list-item
      v-for="(it, i) in localItems"
      :key="it.id"
      class="draggable-item mb-1"
      variant="outlined"
      :draggable="!disabled"
      :rounded="true"
      density="compact"
      @dragstart="disabled ? null : dragStart(i, $event)"
      @dragover.prevent="disabled ? null : undefined"
      @drop="disabled ? null : drop(i, $event)"
    >
      <template v-slot:prepend>
        <v-avatar color="grey-lighten-3" class="mr-n2" size="small">
          <v-icon :style="{ cursor: disabled ? 'default' : 'grab' }">mdi-drag</v-icon>
        </v-avatar>
      </template>

      <v-list-item-title>{{ it.title || it.name }}</v-list-item-title>
      <v-list-item-subtitle v-if="it.subtitle || it.description">
        {{ it.subtitle || it.description }}
      </v-list-item-subtitle>

      <!-- <v-spacer></v-spacer> -->
      <template v-slot:append>
        <v-btn
          class="ml-1"
          icon
          small
          @click.stop="moveUp(i)"
          :disabled="disabled || i === 0"
          aria-label="Sposta su"
          density="compact"
          color="primary"
          variant="outlined"
        >
          <v-icon>mdi-arrow-up-thin</v-icon>
        </v-btn>
        <v-btn
          class="ml-1"
          icon
          small
          @click.stop="moveDown(i)"
          :disabled="disabled || i === localItems.length - 1"
          aria-label="Sposta giù"
          density="compact"
          color="primary"
          variant="outlined"
        >
          <v-icon>mdi-arrow-down-thin</v-icon>
        </v-btn>
        <v-btn
          class="ml-1"
          icon
          color="secondary"
          @click.stop="requestRemove(i)"
          :disabled="disabled"
          aria-label="Elimina"
          density="compact"
          variant="text"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-list-item>
  </v-list>
</template>

<script setup>
import { ref, watch } from "vue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:modelValue", "reorder", "request-remove"]);

function normalizeItems(arr) {
  return (arr || []).map((it, idx) => {
    if (typeof it === "string") {
      // create a stable-ish id from index + base64 of name (shortened)
      let safe;
      try {
        // This converts %XX sequences produced by encodeURIComponent into raw bytes
        // before calling btoa, preserving Unicode correctly.
        const binary = encodeURIComponent(it).replace(
          /%([0-9A-F]{2})/g,
          (match, p1) => String.fromCharCode(parseInt(p1, 16)),
        );
        safe = btoa(binary).slice(0, 8);
      } catch {
        safe = String(idx);
      }
      return { id: `s-${idx}-${safe}`, name: it };
    }
    if (it && typeof it === "object") {
      return {
        id: it.id ?? `o-${idx}`,
        name: it.name ?? it.title ?? String(it),
        title: it.title,
        subtitle: it.subtitle ?? it.description,
      };
    }
    return { id: `o-${idx}`, name: String(it) };
  });
}

const localItems = ref(normalizeItems(props.modelValue));
let draggingIndex = null;

watch(
  () => props.modelValue,
  (v) => {
    localItems.value = normalizeItems(v);
  },
  { deep: true },
);

function dragStart(i, ev) {
  if (props.disabled) return;
  draggingIndex = i;
  try {
    ev.dataTransfer.setData("text/plain", String(i));
  } catch {
    /* some browsers in strict mode */
  }
}

function drop(i, ev) {
  if (props.disabled) return;
  let from = draggingIndex;
  try {
    const dt =
      ev.dataTransfer &&
      ev.dataTransfer.getData &&
      ev.dataTransfer.getData("text/plain");
    if (dt !== undefined && dt !== null && dt !== "") from = parseInt(dt, 10);
  } catch {
    /* ignore malformed drag data */
  }
  if (from == null || isNaN(from)) return;

  const arr = localItems.value;
  arr.splice(i, 0, arr.splice(from, 1)[0]);
  draggingIndex = null;
  emit("update:modelValue", [...arr]);
  emit("reorder", [...arr]);
}

function moveUp(i) {
  if (props.disabled || i <= 0) return;
  const arr = localItems.value;
  arr.splice(i - 1, 0, arr.splice(i, 1)[0]);
  emit("update:modelValue", [...arr]);
}

function moveDown(i) {
  if (props.disabled) return;
  const arr = localItems.value;
  if (i >= arr.length - 1) return;
  arr.splice(i + 1, 0, arr.splice(i, 1)[0]);
  emit("update:modelValue", [...arr]);
}

function requestRemove(i) {
  if (props.disabled) return;
  const arr = localItems.value;
  if (i < 0 || i >= arr.length) return;
  const removed = arr[i];
  // Emit a removal request; parent/wrapper should confirm and actually update the model
  emit("request-remove", { index: i, item: removed });
}
</script>

<style scoped>
.draggable-item {
  user-select: none;
}
</style>
